import React, { useState, useEffect } from "react";
import "./ResetPassword.css";
import { useTranslation } from "react-i18next";
import Button from "../../OwlyManage/components/Buttons/Button/Button";

export default function ResetPassword({ onCancel, uid, token }) {
  const { t } = useTranslation('auth');
  const [new_password, setNewPassword] = useState("");
  const [new_password_confirm, setNewPasswordConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [countdown, setCountdown] = useState(null);

  // Efecto para manejar la cuenta regresiva
  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      onCancel(); // Redirige al login cuando llega a 0
    }
  }, [countdown, onCancel]);

  // Validar que uid y token estén presentes
  if (!uid || !token) {
    return (
      <div className="reset-container">
        <h2 className="reset-title">{t('reset.title')}</h2>
        <div className="reset-error">
          {t('reset.invalid_link')}
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={onCancel}
          style={{ marginTop: 16 }}
        >
          {t('reset.go_login')}
        </Button>
      </div>
    );
  }

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!new_password || !new_password_confirm) {
      setError(t('reset.errors.complete_all'));
      return;
    }
    try {
      const signupUrl =
        process.env.NODE_ENV === "production"
          ? process.env.REACT_APP_BASE_URL_PROD +
            `/api/auth/password-reset-confirm/${uid}/${token}/`
          : process.env.REACT_APP_BASE_URL_DEV +
            `/api/auth/password-reset-confirm/${uid}/${token}/`;
      const response = await fetch(signupUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          new_password,
          new_password_confirm,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        if (typeof data === "object" && data !== null && !Array.isArray(data)) {
          const fieldLabels = {
            new_password: t('reset.errors.fieldLabels.new_password'),
            new_password_confirm: t('reset.errors.fieldLabels.new_password_confirm'),
            password: t('reset.errors.fieldLabels.password'),
          };
          const mensajes = Object.entries(data).flatMap(([field, msgs]) => {
            const label = fieldLabels[field] || field;
            return Array.isArray(msgs)
              ? msgs.map((msg) => `${label}: ${msg}`)
              : [`${label}: ${msgs}`];
          });
          setError(mensajes);
        } else if (data.message) {
          setError(data.message);
        } else {
          setError(t('reset.errors.generic_try_again'));
        }
        return;
      }
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message || data.detail || t('reset.success.reset_ok'));
        
        // Iniciar cuenta regresiva de 3 segundos
        setCountdown(3);
        
        return;
      } else {
        setError(data.message || data.detail || JSON.stringify(data));
      }
    } catch (err) {
      setError(t('reset.errors.generic'));
    }
  };

  return (
    <div className="reset-container">
      <h2 className="reset-title">{t('reset.title')}</h2>
      {success ? (
        <div className="reset-success-block">
          <div className="reset-success">{success}</div>
          <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
            {countdown !== null ? 
              t('reset.redirect_in', { seconds: countdown }) : 
              t('reset.redirect_wait')
            }
          </p>
          <Button
            variant="primary"
            size="md"
            onClick={onCancel}
            style={{ marginTop: 16 }}
          >
            {t('reset.go_login_now')}
          </Button>
        </div>
      ) : (
        <>
          <p className="reset-text">{t('reset.intro')}</p>
          <form onSubmit={handleResetPassword} className="reset-form">
            <label htmlFor="password">{t('reset.fields.password_label')}</label>
            <div className="password-input-wrapper-reset">
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t('reset.fields.password_placeholder')}
                value={new_password}
                onChange={(e) => setNewPassword(e.target.value)}
                className="reset-input-password"
                required
              />
              <button
                type="button"
                className="password-toggle-btn-auth"
                aria-label={t('reset.fields.password_label')}
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={0}
              >
                {showPassword ? (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#888"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 1l22 22" />
                    <path d="M17.94 17.94A10.05 10.05 0 0 1 12 19C7 19 2.73 15.11 1 12c.74-1.32 1.81-2.87 3.22-4.29M9.53 9.53A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5c0 .47-.09.91-.26 1.32" />
                  </svg>
                ) : (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#888"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <ellipse cx="12" cy="12" rx="10" ry="7" />
                    <circle cx="12" cy="12" r="3.5" />
                  </svg>
                )}
              </button>
            </div>
            <label htmlFor="password_confirm">{t('reset.fields.password_confirm_label')}</label>
            <div className="password-input-wrapper-reset">
              <input
                type={showPasswordConfirm ? "text" : "password"}
                placeholder={t('reset.fields.password_confirm_placeholder')}
                value={new_password_confirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                className="reset-input-password"
                required
              />
              <button
                type="button"
                className="password-toggle-btn-auth"
                aria-label={t('reset.fields.password_confirm_label')}
                onClick={() => setShowPasswordConfirm((v) => !v)}
                tabIndex={0}
              >
                {showPasswordConfirm ? (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#888"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 1l22 22" />
                    <path d="M17.94 17.94A10.05 10.05 0 0 1 12 19C7 19 2.73 15.11 1 12c.74-1.32 1.81-2.87 3.22-4.29M9.53 9.53A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5c0 .47-.09.91-.26 1.32" />
                  </svg>
                ) : (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#888"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <ellipse cx="12" cy="12" rx="10" ry="7" />
                    <circle cx="12" cy="12" r="3.5" />
                  </svg>
                )}
              </button>
            </div>
            {Array.isArray(error) ? (
              <ul className="reset-error-list">
                {error.map((msg, idx) => (
                  <li key={idx} className="reset-error">
                    {msg}
                  </li>
                ))}
              </ul>
            ) : (
              error && <div className="reset-error">{error}</div>
            )}
            <div className="reset-buttons">
              <Button type="submit" variant="primary" size="md" className="reset-button">
                {t('reset.confirm')}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="md"
                className="reset-button"
                onClick={() => onCancel()}
              >
                {t('reset.cancel')}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
